export const asNumber = (value) =>
    Number.isFinite(Number(value)) ? Number(value) : 0;
export const asText = (value) => String(value ?? "");
export const advertiserOf = (row) => asText(row.orderAdvertiser);
export const advertiserLabel = (value) => value || "Unknown advertiser";
const sum = (rows, field) =>
    rows.reduce((total, row) => total + asNumber(row[field]), 0);
const change = (current, previous) =>
    previous > 0 ? ((current - previous) / previous) * 100 : current - previous;
const rates = (totals) => ({
    ...totals,
    cpm: totals.impressions ? (totals.revenue / totals.impressions) * 1000 : 0,
    ctr: totals.impressions ? (totals.clicks / totals.impressions) * 100 : 0,
    fillRate: totals.adsRequests
        ? (totals.impressions / totals.adsRequests) * 100
        : 0,
    viewability: totals.impressions
        ? (totals.viewableImpressions / totals.impressions) * 100
        : 0,
});
const additive = [
    "revenue",
    "impressions",
    "clicks",
    "adsRequests",
    "viewableImpressions",
];
const totalsOf = (rows) =>
    rates(
        Object.fromEntries(additive.map((field) => [field, sum(rows, field)])),
    );
const splitBy = (rows, keyOf) => {
    const result = new Map();
    rows.forEach((row) => {
        const key = keyOf(row);
        if (!result.has(key)) result.set(key, []);
        result.get(key).push(row);
    });
    return result;
};
export const dailyTotals = (rows) =>
    [
        ...splitBy(
            rows.filter((row) => row.date),
            (row) => asText(row.date),
        ),
    ]
        .map(([date, items]) => ({ date, ...totalsOf(items) }))
        .sort((a, b) => a.date.localeCompare(b.date));
const summarize = (rows, totalRevenue) => {
    const totals = totalsOf(rows);
    return {
        ...totals,
        revenueShare: totalRevenue ? (totals.revenue / totalRevenue) * 100 : 0,
        revenueChart: dailyTotals(rows),
    };
};

export const prepareAdvertiserRows = (rows, dimension) => {
    const totalRevenue = sum(rows, "revenue");
    return [...splitBy(rows, advertiserOf)].map(([advertiser, items]) => {
        const parent = {
            ...summarize(items, totalRevenue),
            key: JSON.stringify(["advertiser", advertiser]),
            dimension: advertiserLabel(advertiser),
            orderAdvertiser: advertiser,
            isAdvertiser: true,
        };
        if (dimension && dimension !== "orderAdvertiser") {
            parent.children = [
                ...splitBy(items, (row) => asText(row[dimension])),
            ].map(([value, details]) => ({
                ...summarize(details, totalRevenue),
                key: JSON.stringify(["detail", advertiser, value]),
                dimension: value || "Unknown",
                rawDimension: value,
                orderAdvertiser: advertiser,
                isAdvertiser: false,
            }));
        }
        return parent;
    });
};

export const aggregateComparison = (rows) => {
    const totals = (index) =>
        rates(
            Object.fromEntries(
                additive.map((metric) => [
                    metric,
                    rows.reduce(
                        (total, row) => total + asNumber(row[metric]?.[index]),
                        0,
                    ),
                ]),
            ),
        );
    const current = totals(0);
    const reference = totals(2);
    return Object.fromEntries(
        Object.keys(current).map((metric) => [
            metric,
            [
                current[metric],
                change(current[metric], reference[metric]),
                reference[metric],
            ],
        ]),
    );
};

export const prepareComparisonRows = (rows, dimension, grouped) => {
    if (!grouped)
        return [
            ...splitBy(rows, (row) =>
                dimension ? asText(row[dimension]) : "Total",
            ),
        ].map(([value, items]) => ({
            ...aggregateComparison(items),
            key: JSON.stringify(["flat", value]),
            dimension: value || "Unknown",
            rawDimension: value,
            ...(dimension === "orderAdvertiser"
                ? { orderAdvertiser: value, isAdvertiser: true }
                : {}),
        }));
    return [...splitBy(rows, advertiserOf)].map(([advertiser, items]) => {
        const parent = {
            ...aggregateComparison(items),
            key: JSON.stringify(["advertiser", advertiser]),
            dimension: advertiserLabel(advertiser),
            orderAdvertiser: advertiser,
            isAdvertiser: true,
        };
        if (dimension && dimension !== "orderAdvertiser") {
            parent.children = [
                ...splitBy(items, (row) => asText(row[dimension])),
            ].map(([value, details]) => ({
                ...aggregateComparison(details),
                key: JSON.stringify(["detail", advertiser, value]),
                dimension: value || "Unknown",
                rawDimension: value,
                orderAdvertiser: advertiser,
                isAdvertiser: false,
            }));
        }
        return parent;
    });
};

export const filterRows = (rows, searchText, grouped) => {
    const query = searchText.trim().toLowerCase();
    if (!query) return rows;
    if (!grouped)
        return rows.filter((row) =>
            asText(row.dimension).toLowerCase().includes(query),
        );
    return rows.reduce((result, parent) => {
        if (asText(parent.dimension).toLowerCase().includes(query))
            result.push(parent);
        else {
            const children = parent.children?.filter((child) =>
                asText(child.dimension).toLowerCase().includes(query),
            );
            if (children?.length) result.push({ ...parent, children });
        }
        return result;
    }, []);
};

// Preserve the selected chart series while collapsing duplicate date/dimension rows.
export const prepareChartRows = (rows, dimension) =>
    [
        ...splitBy(rows, (row) => (dimension ? asText(row[dimension]) : "")),
    ].flatMap(([value, items]) =>
        dailyTotals(items).map((row) => ({
            ...row,
            ...(dimension ? { [dimension]: value } : {}),
        })),
    );

export const compareTableRows = (a, b, field, comparison) => {
    if (field === "dimension")
        return asText(a.dimension).localeCompare(asText(b.dimension));
    if (field === "revenueShare")
        return (
            (parseFloat(a.revenueShare) || 0) -
            (parseFloat(b.revenueShare) || 0)
        );
    return comparison
        ? asNumber(a[field]?.[0]) - asNumber(b[field]?.[0])
        : asNumber(a[field]) - asNumber(b[field]);
};
export const childNumberMap = (rows, sorting, comparison) => {
    const numbers = new Map();
    rows.forEach((parent) => {
        const children = [...(parent.children || [])];
        if (sorting.order)
            children.sort(
                (a, b) =>
                    compareTableRows(a, b, sorting.field, comparison) *
                    (sorting.order === "descend" ? -1 : 1),
            );
        children.forEach((child, index) => numbers.set(child.key, index + 1));
    });
    return numbers;
};